import { createContext, useContext, useMemo } from "react"
import { resolveTrackColor, trackInactiveColor } from "config/trackColors"

// Provides a per-track highlight color to the session cards, the session detail track tags, and
// the track filter pills without prop drilling. The parent builds the trackName → color map once
// (buildTrackColorMap) and passes it in as `colorMap`. Parallel to RoomColorProvider — the guide
// colors *by room* or *by track* depending on the CMS color-coding setting (see colorCoding.js).
//
// `active` reflects the exclusive color-coding mode: the track set is colored only when coloring
// "byTrack". When inactive, the getter returns the neutral scaffolding gray so track pills/tags
// read as plain labels.
const TrackColorContext = createContext({ getTrackColor: () => trackInactiveColor })

export function TrackColorProvider({ colorMap = {}, active = true, children }) {
    const value = useMemo(
        () => ({
            getTrackColor: track => (active ? resolveTrackColor(colorMap, track) : trackInactiveColor),
        }),
        [colorMap, active],
    )

    return <TrackColorContext.Provider value={value}>{children}</TrackColorContext.Provider>
}

// Single-track convenience. Safe to call outside a provider (returns the neutral gray).
export function useTrackColor(track) {
    return useContext(TrackColorContext).getTrackColor(track)
}

// Returns the resolver function so components can color a *list* of tracks (a session has many)
// without breaking the rules of hooks by calling useTrackColor inside a .map().
export function useTrackColorGetter() {
    return useContext(TrackColorContext).getTrackColor
}
